"use client"

import React, { useState, FormEvent } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getUser, sendMessageToBreeder } from '@/app/actions';
import { toast, ToastContainer } from 'react-toastify'; // Import from react-toastify
import 'react-toastify/dist/ReactToastify.css'; // Import CSS for react-toastify

interface ContactFormProps {
  type?: "message" | "reserve";
  breederId: string; // Added breederId prop
  subject: string; // Added subject prop
}

function ContactForm({ type = "message", breederId, subject }: ContactFormProps) {
  const { data: user, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user'],
    queryFn: () => getUser(),
    staleTime: Infinity,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (data: { breederId: string; subject: string; body: string }) =>
      sendMessageToBreeder(data.breederId, data.subject, data.body),
    onSuccess: () => {
      toast.success('Message sent successfully!'); // Use react-toastify
      setIsModalOpen(false);
      setMessage(""); // Clear message on success
      setAgreedToPolicy(false); // Reset policy agreement
      setFormError(null); // Clear any previous errors
    },
    onError: (error: any) => {
      console.error("Failed to send message:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to send message. Please try again.";
      setFormError(errorMessage);
      toast.error(errorMessage); // Use react-toastify
    },
  });

  const handleModalToggle = () => {
    if (!isLoadingUser && !user) {
      // Trigger sign-up/login modal if user is not logged in
      const signUpButton = document.getElementById("sign-up-modal") as HTMLButtonElement | null;
      if (signUpButton) {
        signUpButton.click();
      } else {
        console.warn("Sign-up modal button not found");
        toast.error("Please log in or sign up to contact the breeder."); // Use react-toastify
      }
      return;
    }

    setIsModalOpen(!isModalOpen);
    setFormError(null); // Clear errors when opening/closing modal
    if (!isModalOpen) { // If opening the modal
      if (type === "reserve") {
        setMessage("Hey, could I please reserve this?");
      } else {
        setMessage(""); // Reset message for standard message type
      }
      setAgreedToPolicy(false); // Reset policy agreement when opening
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null); // Clear previous errors

    if (!agreedToPolicy) {
      const errorMsg = "You must agree to the privacy policy.";
      setFormError(errorMsg);
      toast.error(errorMsg); // Use react-toastify
      return;
    }

    if (!message.trim()) {
      const errorMsg = "Message cannot be empty.";
      setFormError(errorMsg);
      toast.error(errorMsg); // Use react-toastify
      return;
    }

    if (!breederId || !subject) {
        const errorMsg = "Missing required information (breeder or subject).";
        setFormError(errorMsg);
        toast.error("Cannot send message: Missing required information."); // Use react-toastify
        console.error("Missing breederId or subject", { breederId, subject });
        return;
    }

    mutation.mutate({ breederId, subject, body: message });
  };

  return (
    <div>
      {/* Make sure ToastContainer is rendered somewhere in your app, often in the root layout */}
      {/* <ToastContainer /> */}
      <button
        className={`btn w-full capitalize ${type === "reserve" ? "btn-secondary" : "btn-primary"}`}
        onClick={handleModalToggle}
        disabled={isLoadingUser} // Disable button while checking user status
      >
        {isLoadingUser ? "Loading..." : (type === "reserve" ? "Ask to Reserve" : "Message Breeder")}
      </button>

      {isModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box relative"> {/* Added relative positioning for close button */}
            <button
              className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
              onClick={handleModalToggle}
              disabled={mutation.isPending}
              aria-label="Close" // Added aria-label for accessibility
            >
              ✕
            </button>
            <h2 className="font-bold text-lg mb-4">Contact Breeder</h2>
            <form onSubmit={handleSubmit}>
              {/* Removed Name, Email, Phone fields as the message is sent by the logged-in user */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Your Message</span>
                </label>
                <textarea
                  className="textarea textarea-bordered h-32" // Increased height
                  placeholder="Write your message here..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  disabled={mutation.isPending}
                ></textarea>
              </div>
              <div className="form-control mt-4">
                <label className="label cursor-pointer justify-start items-center"> {/* Improved alignment */}
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary mr-2" // Added margin
                    checked={agreedToPolicy}
                    onChange={(e) => setAgreedToPolicy(e.target.checked)}
                    disabled={mutation.isPending}
                  />
                  <span className="label-text">
                    By submitting this form I agree to the privacy policy
                  </span>
                </label>
              </div>

              {formError && (
                <div role="alert" className="text-error text-sm mt-2">{formError}</div> // Added role="alert"
              )}

              <div className="modal-action mt-6">
                <button
                  type="button"
                  className="btn"
                  onClick={handleModalToggle}
                  disabled={mutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn btn-primary ${mutation.isPending ? "loading" : ""}`}
                  disabled={mutation.isPending || !agreedToPolicy}
                >
                  {mutation.isPending ? "Sending..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
           {/* Optional: Click outside to close */}
           <div className="modal-backdrop" onClick={!mutation.isPending ? handleModalToggle : undefined}></div>
        </div>
      )}
    </div>
  );
}

export default ContactForm;